import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox, CheckboxGroup } from '../../../components/ui/Checkbox';
import Input from '../../../components/ui/Input';

const BulkExportPanel = ({ className = '' }) => {
  const [selectedReports, setSelectedReports] = useState([]);
  const [bulkFormat, setBulkFormat] = useState('xlsx');
  const [compressionType, setCompressionType] = useState('zip');
  const [emailDelivery, setEmailDelivery] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);

  const availableReports = [
    {
      id: 'best_price',
      name: 'Best Price Analysis',
      description: 'Products with lowest prices from available suppliers',
      estimatedSize: '2.4 MB',
      estimatedTime: '30 seconds'
    },
    {
      id: 'all_groups',
      name: 'Complete Product Groups',
      description: 'All matched product groups with supplier details',
      estimatedSize: '8.7 MB',
      estimatedTime: '2 minutes'
    },
    {
      id: 'ambiguous',
      name: 'Ambiguous Matches',
      description: 'Products requiring manual review and verification',
      estimatedSize: '1.2 MB',
      estimatedTime: '45 seconds'
    },
    {
      id: 'unmatched',
      name: 'Unmatched Products',
      description: 'Products that could not be matched across suppliers',
      estimatedSize: '456 KB',
      estimatedTime: '15 seconds'
    },
    {
      id: 'supplier_summary',
      name: 'Supplier Performance Summary',
      description: 'Analytics and metrics for each supplier',
      estimatedSize: '890 KB',
      estimatedTime: '20 seconds'
    },
    {
      id: 'price_trends',
      name: 'Price Trend Analysis',
      description: 'Historical price changes and market trends',
      estimatedSize: '3.1 MB',
      estimatedTime: '1 minute'
    }
  ];

  const formatOptions = [
    { value: 'xlsx', label: 'Excel Workbook (.xlsx)' },
    { value: 'csv', label: 'CSV Files (.csv)' },
    { value: 'json', label: 'JSON Files (.json)' }
  ];

  const compressionOptions = [
    { value: 'zip', label: 'ZIP Archive (.zip)' },
    { value: 'tar', label: 'TAR Archive (.tar.gz)' },
    { value: 'none', label: 'No Compression' }
  ];

  const handleReportToggle = (reportId) => {
    setSelectedReports(prev => 
      prev?.includes(reportId)
        ? prev?.filter(id => id !== reportId)
        : [...prev, reportId]
    );
  };

  const selectAllReports = () => {
    setSelectedReports(availableReports?.map(report => report?.id));
  };

  const clearAllReports = () => {
    setSelectedReports([]);
  };

  const calculateTotalSize = () => {
    const selectedReportData = availableReports?.filter(report => 
      selectedReports?.includes(report?.id)
    );
    const totalMB = selectedReportData?.reduce((total, report) => {
      const size = parseFloat(report?.estimatedSize?.replace(/[^\d.]/g, ''));
      const unit = report?.estimatedSize?.includes('KB') ? 0.001 : 1;
      return total + (size * unit);
    }, 0);
    return totalMB?.toFixed(1) + ' MB';
  };

  const calculateTotalTime = () => {
    const selectedReportData = availableReports?.filter(report => 
      selectedReports?.includes(report?.id)
    );
    const totalSeconds = selectedReportData?.reduce((total, report) => {
      const timeStr = report?.estimatedTime;
      if (timeStr?.includes('minute')) {
        return total + (parseInt(timeStr) * 60);
      }
      return total + parseInt(timeStr);
    }, 0);
    
    if (totalSeconds < 60) return `${totalSeconds} seconds`;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes} minutes`;
  };

  const handleBulkGenerate = () => {
    if (selectedReports?.length === 0) return;
    
    setIsGenerating(true);
    setProgress(0);
    
    // Simulate progress
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsGenerating(false);
          return 100;
        }
        return prev + Math.random() * 15;
      });
    }, 500);
  };

  return (
    <div className={`bg-card border border-border rounded-lg ${className}`}>
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Bulk Export</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Generate multiple reports in a single download package
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={selectAllReports}
              iconName="CheckSquare"
              iconPosition="left"
            >
              Select All
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllReports}
              iconName="Square"
              iconPosition="left"
            >
              Clear All
            </Button>
          </div>
        </div>
      </div>
      {/* Report Selection */}
      <div className="p-6 border-b border-border">
        <h3 className="text-lg font-medium text-foreground mb-4">Select Reports to Include</h3>
        <CheckboxGroup>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {availableReports?.map((report) => (
              <div key={report?.id} className="border border-border rounded-lg p-4 hover:bg-muted hover:bg-opacity-50 transition-colors">
                <Checkbox
                  label={report?.name}
                  description={report?.description}
                  checked={selectedReports?.includes(report?.id)}
                  onChange={() => handleReportToggle(report?.id)}
                  className="mb-2"
                />
                <div className="ml-6 text-xs text-muted-foreground space-y-1">
                  <div>Size: {report?.estimatedSize}</div>
                  <div>Est. time: {report?.estimatedTime}</div>
                </div>
              </div>
            ))}
          </div>
        </CheckboxGroup>
      </div>
      {/* Configuration */}
      <div className="p-6 border-b border-border">
        <h3 className="text-lg font-medium text-foreground mb-4">Export Configuration</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Select
            label="File Format"
            options={formatOptions}
            value={bulkFormat}
            onChange={setBulkFormat}
          />
          <Select
            label="Compression"
            options={compressionOptions}
            value={compressionType}
            onChange={setCompressionType}
          />
        </div>
        
        <Checkbox
          label="Email delivery"
          description="Send download link via email when ready"
          checked={emailDelivery}
          onChange={(e) => setEmailDelivery(e?.target?.checked)}
          className="mb-4"
        />
        
        {emailDelivery && (
          <Input
            label="Email Address"
            type="email"
            placeholder="your.email@company.com"
            value={emailAddress}
            onChange={(e) => setEmailAddress(e?.target?.value)}
            className="mb-4"
          />
        )}
      </div>
      {/* Summary & Actions */}
      <div className="p-6">
        {selectedReports?.length > 0 && (
          <div className="bg-muted bg-opacity-50 rounded-lg p-4 mb-6">
            <h4 className="font-medium text-foreground mb-2">Export Summary</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Reports:</span>
                <span className="ml-2 font-medium text-foreground">{selectedReports?.length}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Total Size:</span>
                <span className="ml-2 font-medium text-foreground">{calculateTotalSize()}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Est. Time:</span>
                <span className="ml-2 font-medium text-foreground">{calculateTotalTime()}</span>
              </div>
            </div>
          </div>
        )}

        {/* Progress Bar */}
        {isGenerating && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">Generating Reports...</span>
              <span className="text-sm text-muted-foreground">{Math.round(progress)}%</span>
            </div>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary transition-all duration-300 ease-smooth"
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Processing {selectedReports?.length} reports...
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {selectedReports?.length === 0 
              ? 'Select reports to enable bulk export'
              : `${selectedReports?.length} report${selectedReports?.length !== 1 ? 's' : ''} selected`
            }
          </div>
          <div className="flex space-x-3">
            <Button
              variant="outline"
              iconName="Eye"
              iconPosition="left"
              disabled={selectedReports?.length === 0}
            >
              Preview Package
            </Button>
            <Button
              variant="default"
              onClick={handleBulkGenerate}
              loading={isGenerating}
              disabled={selectedReports?.length === 0}
              iconName="Package"
              iconPosition="left"
            >
              {isGenerating ? 'Generating...' : 'Generate Package'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkExportPanel;